import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChartLine, Trophy, ArrowRight, ArrowUpRight, Sparkle,
  Star, CaretLeft, ChartLineUp, Rocket, ShieldWarning, Crown, ShareNetwork,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const shareSeoPage = async (path, copiedMsg) => {
  const shareUrl = `${BACKEND_URL}${path}`;
  if (navigator.share) {
    try { await navigator.share({ url: shareUrl }); return; } catch { /* user cancelled */ }
  }
  try {
    await navigator.clipboard.writeText(shareUrl);
    toast.success(copiedMsg);
  } catch { /* clipboard unavailable */ }
};

const CLASS_MAP = {
  excelente: { color: '#10B981' },
  boa: { color: '#3B82F6' },
  moderada: { color: '#F59E0B' },
  evitar: { color: '#EF4444' },
};

const LEVEL_MAP = {
  baixo: '#10B981', medio: '#F59E0B', alto: '#3B82F6', muito_alto: '#A855F7',
};

const scoreColor = (s) => (s >= 90 ? '#10B981' : s >= 80 ? '#3B82F6' : s >= 70 ? '#F59E0B' : '#EF4444');

const handleLogin = () => {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const ScorePillBig = ({ score }) => {
  const color = scoreColor(score);
  return (
    <div
      className="flex-shrink-0 w-20 h-20 rounded-2xl flex flex-col items-center justify-center border-2"
      style={{ background: `${color}15`, borderColor: `${color}55` }}
    >
      <div className="text-2xl font-[Outfit] font-bold leading-none" style={{ color }}>{score}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">/ 100</div>
    </div>
  );
};

export const WinnerCard = ({ item }) => {
  const { t } = useTranslation();
  const clsKey = CLASS_MAP[item.classification] ? item.classification : 'moderada';
  const cls = CLASS_MAP[clsKey];
  return (
    <div className="p-5 bg-[#0A0A0A] border border-white/10 rounded-2xl hover:border-white/20 transition group" data-testid="hall-card">
      <div className="flex items-start gap-4 mb-4">
        <ScorePillBig score={item.winner_score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
              style={{ background: `${cls.color}22`, color: cls.color }}
            >
              {t(`hall.class_${clsKey}`)}
            </span>
            <span className="font-mono text-[10px] text-white/30">{item.domain_hint}</span>
          </div>
          {item.context && (
            <div className="text-xs text-white/50 mb-1.5 line-clamp-1">{item.context}</div>
          )}
          <p className="text-sm text-white/80 leading-relaxed line-clamp-3">{item.summary || t('hall.summary_fallback')}</p>
        </div>
      </div>

      {item.top_strength && (
        <div className="px-3 py-2 bg-[#10B981]/10 border border-[#10B981]/20 rounded-lg mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-semibold mb-0.5">{t('hall.top_strength')}</div>
          <p className="text-xs text-white/80 line-clamp-2">{item.top_strength}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">{t('hall.profit')}</div>
          <div className="text-xs font-semibold" style={{ color: LEVEL_MAP[item.profit_potential] || '#9CA3AF' }}>
            {item.profit_potential ? t(`ws.lvl_${item.profit_potential}`, { defaultValue: item.profit_potential }) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">{t('hall.scale')}</div>
          <div className="text-xs font-semibold" style={{ color: LEVEL_MAP[item.scalability_potential] || '#9CA3AF' }}>
            {item.scalability_potential ? t(`ws.lvl_${item.scalability_potential}`, { defaultValue: item.scalability_potential }) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">{t('hall.risk')}</div>
          <div className="text-xs font-semibold" style={{ color: LEVEL_MAP[item.risk_level] || '#9CA3AF' }}>
            {item.risk_level ? t(`ws.lvl_${item.risk_level}`, { defaultValue: item.risk_level }) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
};

const HallOfWinners = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [res, nres] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/hall-of-winners?limit=30&min_score=70`),
        axios.get(`${BACKEND_URL}/api/public/winners/niches`).catch(() => ({ data: { niches: [] } })),
      ]);
      setItems(res.data.items || []);
      setStats(res.data.stats);
      setNiches(nres.data.niches || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    document.title = 'Hall of Winners™ · FunnelHack AI';
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="hall-of-winners-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="hall-home-link">
            <div className="w-10 h-10 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
              <ChartLine size={24} weight="bold" className="text-white" />
            </div>
            <span className="text-xl font-[Outfit] font-bold tracking-tight">FunnelHack AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="hall-back-btn" className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-1.5">
              <CaretLeft size={14} weight="bold" /> {t('hall.back')}
            </Link>
            <button onClick={handleLogin} data-testid="hall-cta-header" className="px-5 py-2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-lg text-sm font-semibold">
              {t('hall.trial')}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-36 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#F59E0B]/10 blur-[160px]" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-6">
            <Trophy size={14} weight="fill" className="text-[#F59E0B]" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-[#F59E0B] font-semibold">{t('hall.kicker')}</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-[Outfit] font-bold tracking-tight mb-5">
            Hall of <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Winners™</span>
          </h1>
          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-8">
            {t('hall.hero_sub')}
          </p>

          {stats && (
            <div className="inline-flex flex-wrap items-center justify-center gap-6 px-6 py-3 bg-white/[0.03] border border-white/10 rounded-2xl mb-4">
              <div className="text-center">
                <div className="text-2xl font-[Outfit] font-bold text-white" data-testid="hall-stats-total">{stats.total_analyses}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('hall.stats_total')}</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-2xl font-[Outfit] font-bold" style={{ color: scoreColor(stats.average_score) }} data-testid="hall-stats-avg">{stats.average_score || '—'}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('hall.stats_avg')}</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-2xl font-[Outfit] font-bold text-[#F59E0B]" data-testid="hall-stats-showing">{stats.showing}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('hall.stats_showing')}</div>
              </div>
            </div>
          )}

          <div className="mt-2">
            <button
              onClick={() => shareSeoPage('/api/og/hall', t('hall.share_copied'))}
              data-testid="hall-share-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F59E0B]/40 rounded-lg text-sm text-white/70 hover:text-white transition"
            >
              <ShareNetwork size={14} weight="bold" className="text-[#F59E0B]" />
              {t('hall.share_btn')}
            </button>
          </div>
        </div>
      </section>

      {/* Niche pages (SEO hub) */}
      {niches.length > 0 && (
        <section className="px-6 pb-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-3">{t('hall.explore_niche')}</div>
            <div className="flex flex-wrap justify-center gap-2" data-testid="hall-niche-chips">
              {niches.map((n) => (
                <Link key={n.slug} to={`/winners/${n.slug}`}
                  className="px-3.5 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F59E0B]/40 rounded-lg text-sm text-white/70 hover:text-white transition">
                  {n.label}{n.count > 0 ? ` (${n.count})` : ''}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-white/40" data-testid="hall-loading">{t('hall.loading')}</div>
          ) : items.length === 0 ? (
            <div className="max-w-2xl mx-auto p-10 bg-[#0A0A0A] border border-dashed border-white/15 rounded-3xl text-center" data-testid="hall-empty">
              <Trophy size={40} weight="bold" className="text-white/20 mx-auto mb-4" />
              <h3 className="text-2xl font-[Outfit] font-bold mb-2">{t('hall.empty_title')}</h3>
              <p className="text-white/60 mb-6 leading-relaxed">
                {t('hall.empty_sub')}
              </p>
              <button
                onClick={handleLogin}
                data-testid="hall-empty-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] rounded-xl text-sm font-semibold text-black"
              >
                <Trophy size={14} weight="fill" />
                {t('hall.empty_cta')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((it) => <WinnerCard key={it.score_id} item={it} />)}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto p-10 bg-gradient-to-br from-[#F59E0B]/10 via-[#3B82F6]/5 to-transparent border border-[#F59E0B]/30 rounded-3xl text-center">
          <Trophy size={36} weight="fill" className="text-[#F59E0B] mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold mb-3">{t('hall.cta_title')}</h2>
          <p className="text-white/60 mb-7 max-w-xl mx-auto leading-relaxed">
            {t('hall.cta_sub')}
          </p>
          <button
            onClick={handleLogin}
            data-testid="hall-final-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:opacity-90 rounded-xl text-base font-semibold text-black transition shadow-2xl shadow-amber-500/20"
          >
            <Rocket size={20} weight="fill" />
            {t('hall.cta_btn')}
            <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      <footer className="px-6 py-8 border-t border-white/10 text-sm text-white/40">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} FunnelHack AI · {t('hall.footer_tm')}</div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-white transition">{t('hall.footer_home')}</Link>
            <Link to="/guia" className="hover:text-white transition">{t('hall.footer_guide')}</Link>
            <a href="/#pricing" className="hover:text-white transition">{t('hall.footer_pricing')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HallOfWinners;
